import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_ENDPOINT } from '../utils/api';
import { Activity, AlertTriangle, CheckCircle, FileText } from 'lucide-react';

function PublicFeedbackForm() {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [responses, setResponses] = useState({});
  const [respondentName, setRespondentName] = useState('');
  const [respondentRole, setRespondentRole] = useState('');
  const [respondentEmail, setRespondentEmail] = useState('');
  const [respondentOrganization, setRespondentOrganization] = useState('');

  useEffect(() => {
  if (formId) {
    loadForm(formId);
  }
}, [formId]);

  const loadForm = async (id) => {
    try {
      const response = await fetch(`${API_ENDPOINT}/events/feedback-form/${id}`);
      if (!response.ok) {
        throw new Error('Form not found');
      }
      const data = await response.json();
      
      if (data.status !== 'published') {
        alert('This form is not currently accepting responses.');
        setLoading(false);
        return;
      }
      
      setForm(data);
    } catch (error) {
      console.error('Error loading form:', error);
      alert('Failed to load form. Please check the link.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allQuestions = form.sections.flatMap(s => s.questions);
    const requiredQuestions = allQuestions.filter(q => q.required);
    
    for (let question of requiredQuestions) {
      if (!responses[question.question_id] || !responses[question.question_id].toString().trim()) {
        alert(`Please answer the required question: ${question.question_text}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_ENDPOINT}/events/feedback-form/${formId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_id: formId,
          respondent_name: respondentName,
          respondent_role: respondentRole,
          respondent_email: respondentEmail,
          respondent_organization: respondentOrganization,
          responses: responses
        })
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const error = await response.json();
        alert(`Failed to submit: ${error.error}`);
      }
    } catch (error) {
      alert(`Error submitting form: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Form Not Found</h2>
          <p className="text-gray-600">
            This form may have been removed or the link may be incorrect.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-6 text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
            <p className="text-lg text-gray-600 mb-6">
              Your feedback has been submitted successfully.
            </p>
            <p className="text-sm text-gray-500">
              Your input helps us improve our emergency response and preparedness.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{form.form_title}</h1>
          {form.form_description && (
            <p className="text-gray-600 mb-4">{form.form_description}</p>
          )}
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <FileText className="w-4 h-4" />
            <span>All fields marked with * are required</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Respondent Info */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Your Information</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name (Optional)
                </label>
                <input
                  type="text"
                  value={respondentName}
                  onChange={(e) => setRespondentName(e.target.value)}
                  placeholder="Enter your full name (optional)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role/Position (Optional)
                </label>
                <input
                  type="text"
                  value={respondentRole}
                  onChange={(e) => setRespondentRole(e.target.value)}
                  placeholder="e.g., Emergency Manager, First Responder"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  value={respondentEmail}
                  onChange={(e) => setRespondentEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization (Optional)
                </label>
                <input
                  type="text"
                  value={respondentOrganization}
                  onChange={(e) => setRespondentOrganization(e.target.value)}
                  placeholder="e.g., City of Springfield Fire Department"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Questions */}
          {form.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="bg-white rounded-lg shadow-lg p-6">
              {section.section_title && (
                <h3 className="text-lg font-bold text-gray-900 mb-4">{section.section_title}</h3>
              )}

              <div className="space-y-6">
                {section.questions.map((question, questionIndex) => (
                  <div key={question.question_id}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {questionIndex + 1}. {question.question_text}
                      {question.required && <span className="text-red-500 ml-1">*</span>}
                    </label>

                    {/* Short Text */}
                    {question.question_type === 'short_text' && (
                      <input
                        type="text"
                        value={responses[question.question_id] || ''}
                        onChange={(e) => setResponses({
                          ...responses,
                          [question.question_id]: e.target.value
                        })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required={question.required}
                      />
                    )}

                    {/* Long Text */}
                    {question.question_type === 'long_text' && (
                      <textarea
                        value={responses[question.question_id] || ''}
                        onChange={(e) => setResponses({
                          ...responses,
                          [question.question_id]: e.target.value
                        })}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required={question.required}
                      />
                    )}

                    {/* Rating */}
                    {question.question_type === 'rating' && (
                      <div className="flex items-center space-x-4">
                        {[1, 2, 3, 4, 5].map(rating => (
                          <label key={rating} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              name={question.question_id}
                              value={rating}
                              checked={responses[question.question_id] === rating}
                              onChange={() => setResponses({
                                ...responses,
                                [question.question_id]: rating
                              })}
                              className="text-blue-600"
                              required={question.required}
                            />
                            <span className="text-gray-700">{rating}</span>
                          </label>
                        ))}
                        <span className="text-sm text-gray-500 ml-4">(1 = Poor, 5 = Excellent)</span>
                      </div>
                    )}

                    {/* Yes/No */}
                    {question.question_type === 'yes_no' && (
                      <div className="flex items-center space-x-6">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name={question.question_id}
                            value="yes"
                            checked={responses[question.question_id] === 'yes'}
                            onChange={() => setResponses({
                              ...responses,
                              [question.question_id]: 'yes'
                            })}
                            className="text-blue-600"
                            required={question.required}
                          />
                          <span className="text-gray-700">Yes</span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name={question.question_id}
                            value="no"
                            checked={responses[question.question_id] === 'no'}
                            onChange={() => setResponses({
                              ...responses,
                              [question.question_id]: 'no'
                            })}
                            className="text-blue-600"
                            required={question.required}
                          />
                          <span className="text-gray-700">No</span>
                        </label>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Submit Button */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { PublicFeedbackForm };
export default PublicFeedbackForm;